import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, DollarSign, Globe, Image, Palette, Save, RefreshCw, Bell, ExternalLink, MapPin, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PushNotificationManager from '@/components/PushNotificationManager';
import { useAuth } from '@/context/AuthContext';
import CompanyCurrencyEditor from '@/components/CompanyCurrencyEditor';
import CompanyProfileEditor from '@/components/CompanyProfileEditor';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CURRENCIES = [
  { value: 'AED', label: 'AED (د.إ)', symbol: 'د.إ' },
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'EUR', label: 'EUR (€)', symbol: '€' },
  { value: 'GBP', label: 'GBP (£)', symbol: '£' },
  { value: 'SAR', label: 'SAR (ر.س)', symbol: 'ر.س' },
];

const LANGUAGES = [
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'ar', label: 'العربية', flag: '🇦🇪' },
];

const THEMES = [
  { value: 'dark', label: 'Dark Theme', icon: '🌙' },
  { value: 'light', label: 'Light Theme', icon: '☀️' },
];

const SettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.is_super_admin;
  
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    currency: 'AED',
    language: 'en',
    theme: 'dark',
    login_background: null,
    login_title: 'Luxury Furniture,',
    login_subtitle: 'Exceptional Craftsmanship',
    login_description: 'Operations Command Center for Wooden Twist UAE',
    order_sync_interval: 5,
    auto_sync_enabled: true,
    payment_link_gateway: 'stripe',
    auto_pack_on_slip_print: false,
  });
  const [previewImage, setPreviewImage] = useState(null);
  
  // MamoPay settings
  const [mamoSettings, setMamoSettings] = useState({
    configured: false,
    api_key: '',
    api_key_masked: '',
    enable_for_leads: true,
    enable_for_orders: true,
    enable_for_drivers: true,
    send_customer_receipt: true,
    default_description_template: 'Payment for {reference}'
  });
  const [showMamoKey, setShowMamoKey] = useState(false);
  const [testingMamo, setTestingMamo] = useState(false);
  const [savingMamo, setSavingMamo] = useState(false);
  
  // Webhook state
  const [webhookStatus, setWebhookStatus] = useState({
    configured: false,
    webhook_id: null,
    webhook_url: null,
    registered_at: null
  });
  const [registeringWebhook, setRegisteringWebhook] = useState(false);
  const [loadingWebhookStatus, setLoadingWebhookStatus] = useState(false);

  useEffect(() => {
    loadSettings();
    loadMamoSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(response.data);
      setPreviewImage(response.data.login_background);
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const loadMamoSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/settings/mamopay`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMamoSettings(prev => ({ ...prev, ...response.data }));
      
      // Also load webhook status if MamoPay is configured
      if (response.data.configured) {
        loadWebhookStatus();
      }
    } catch (error) {
      console.error('Failed to load MamoPay settings');
    }
  };

  const loadWebhookStatus = async () => {
    setLoadingWebhookStatus(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/mamopay/webhook/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWebhookStatus({
        configured: !!response.data.configured_webhook_id,
        webhook_id: response.data.configured_webhook_id,
        webhook_url: response.data.configured_webhook_url,
        registered_at: response.data.registered_at,
        mamopay_webhooks: response.data.mamopay_webhooks || []
      });
    } catch (error) {
      console.error('Failed to load webhook status');
    } finally {
      setLoadingWebhookStatus(false);
    }
  };

  const handleRegisterWebhook = async () => {
    setRegisteringWebhook(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/mamopay/webhook/register`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Webhook registered successfully! MamoPay will now send payment notifications automatically.');
      setWebhookStatus({
        configured: true,
        webhook_id: response.data.webhook_id,
        webhook_url: response.data.webhook_url,
        registered_at: new Date().toISOString()
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register webhook');
    } finally {
      setRegisteringWebhook(false);
    }
  };

  const handleDeleteWebhook = async () => {
    if (!webhookStatus.webhook_id) return;
    if (!window.confirm('Are you sure you want to remove the webhook? Payment notifications will stop working.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/mamopay/webhook/${webhookStatus.webhook_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Webhook removed');
      setWebhookStatus({
        configured: false,
        webhook_id: null,
        webhook_url: null,
        registered_at: null
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove webhook');
    }
  };

  const handleSaveMamoSettings = async () => {
    if (!mamoSettings.api_key && !mamoSettings.configured) {
      toast.error('Please enter an API key');
      return;
    }
    
    setSavingMamo(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/settings/mamopay`, {
        api_key: mamoSettings.api_key,
        enable_for_leads: mamoSettings.enable_for_leads,
        enable_for_orders: mamoSettings.enable_for_orders,
        enable_for_drivers: mamoSettings.enable_for_drivers,
        send_customer_receipt: mamoSettings.send_customer_receipt,
        default_description_template: mamoSettings.default_description_template
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('MamoPay settings saved successfully');
      loadMamoSettings();
      setShowMamoKey(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save MamoPay settings');
    } finally {
      setSavingMamo(false);
    }
  };

  const handleTestMamoConnection = async () => {
    setTestingMamo(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/settings/mamopay/test`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('MamoPay connection successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingMamo(false);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Log the payload size for debugging
      const payload = {
        ...settings,
        login_background: previewImage,
      };
      console.log('Saving settings, login_background size:', previewImage ? previewImage.length : 0);
      
      await axios.put(
        `${API_BASE_URL}/settings`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Settings saved successfully');
      
      // Apply theme immediately
      if (settings.theme === 'light') {
        document.documentElement.classList.add('light-theme');
      } else {
        document.documentElement.classList.remove('light-theme');
      }
      
      // Store settings in localStorage for immediate access
      localStorage.setItem('app_settings', JSON.stringify(settings));
      
      window.location.reload();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image size must be less than 10MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setPreviewImage(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-playfair text-4xl font-bold text-wt-text-primary mb-2">
          System Settings
        </h1>
        <p className="text-wt-text-secondary">Configure system-wide preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Profile — admins edit their own company details */}
        {(user?.is_admin || user?.roles?.includes('admin') || user?.is_super_admin) && (
          <CompanyProfileEditor />
        )}

        {/* Currency Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <DollarSign className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Currency</h2>
              <p className="text-sm text-wt-text-secondary">Company currency setting</p>
            </div>
          </div>

          <div className="space-y-4">
            {isSuperAdmin ? (
              <>
                <p className="text-wt-text-secondary text-sm">
                  Currency is configured at the company level. You can update it in Company Settings.
                </p>
                <Button
                  variant="outline"
                  className="w-full border-wt-border text-wt-text-primary hover:bg-wt-subtle"
                  onClick={() => navigate('/super-admin/companies')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Go to Company Settings
                </Button>
              </>
            ) : (
              <CompanyCurrencyEditor
                currentCurrency={settings.currency || 'AED'}
                onSaved={(newCurrency) => {
                  setSettings(s => ({ ...s, currency: newCurrency }));
                  toast.success(`Currency updated to ${newCurrency}`);
                }}
              />
            )}
          </div>
        </motion.div>

        {/* Language Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <Globe className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Language</h2>
              <p className="text-sm text-wt-text-secondary">System language</p>
            </div>
          </div>

          <div>
            <Label className="text-wt-text-primary">Select Language</Label>
            <Select
              value={settings.language}
              onValueChange={(value) => setSettings({ ...settings, language: value })}
            >
              <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border">
                {LANGUAGES.map((language) => (
                  <SelectItem
                    key={language.value}
                    value={language.value}
                    className="text-wt-text-primary"
                  >
                    {language.flag} {language.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Theme Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <Palette className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Theme</h2>
              <p className="text-sm text-wt-text-secondary">Visual appearance</p>
            </div>
          </div>

          <div>
            <Label className="text-wt-text-primary">Select Theme</Label>
            <Select
              value={settings.theme}
              onValueChange={(value) => setSettings({ ...settings, theme: value })}
            >
              <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border">
                {THEMES.map((theme) => (
                  <SelectItem
                    key={theme.value}
                    value={theme.value}
                    className="text-wt-text-primary"
                  >
                    {theme.icon} {theme.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Login Page - Super Admin Only */}
        {isSuperAdmin && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-wt-gold/10 border border-wt-gold/20">
                <Image className="h-6 w-6 text-wt-gold" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-wt-text-primary">Login Page</h2>
                <p className="text-sm text-wt-text-secondary">Customize login page appearance and text (Super Admin only)</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Background Image */}
              <div>
                <Label className="text-wt-text-primary mb-2 block">Background Image</Label>
                {previewImage ? (
                  <div className="relative">
                    <img
                      src={previewImage}
                      alt="Login Background Preview"
                      className="w-full h-40 object-cover rounded-lg border border-wt-border"
                    />
                    <button
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 p-2 rounded-full bg-red-500 hover:bg-red-600 text-white"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className="w-full h-40 rounded-lg border-2 border-dashed border-wt-border flex items-center justify-center">
                    <p className="text-wt-text-muted">No image selected</p>
                  </div>
                )}

                <label className="block mt-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-wt-border text-wt-text-primary hover:bg-wt-subtle"
                    onClick={() => document.querySelector('input[type="file"]').click()}
                  >
                    <Image className="h-4 w-4 mr-2" />
                    Upload Background Image
                  </Button>
                </label>
                <p className="text-xs text-wt-text-muted mt-1">Max 10MB. Recommended: 1920x1080px</p>
              </div>

              {/* Login Text Customization */}
              <div className="pt-4 border-t border-wt-border">
                <Label className="text-wt-text-primary mb-2 block">Title Text</Label>
                <Input
                  value={settings.login_title}
                  onChange={(e) => setSettings({ ...settings, login_title: e.target.value })}
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                  placeholder="Luxury Furniture,"
                />
              </div>

              <div>
                <Label className="text-wt-text-primary mb-2 block">Subtitle Text (Gold)</Label>
                <Input
                  value={settings.login_subtitle}
                  onChange={(e) => setSettings({ ...settings, login_subtitle: e.target.value })}
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                  placeholder="Exceptional Craftsmanship"
                />
              </div>

              <div>
                <Label className="text-wt-text-primary mb-2 block">Description</Label>
                <Input
                  value={settings.login_description}
                  onChange={(e) => setSettings({ ...settings, login_description: e.target.value })}
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                  placeholder="Operations Command Center"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Auto Sync Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <RefreshCw className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Order Auto-Sync</h2>
              <p className="text-sm text-wt-text-secondary">Automatic Shopify order sync</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-wt-text-primary">Enable Auto Sync</Label>
                <p className="text-xs text-wt-text-muted">Automatically sync orders from Shopify</p>
              </div>
              <Switch
                checked={settings.auto_sync_enabled}
                onCheckedChange={(value) => setSettings({ ...settings, auto_sync_enabled: value })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-wt-text-primary">Auto-Pack on Slip Print</Label>
                <p className="text-xs text-wt-text-muted">Move orders to Packed automatically when their packing slip is printed</p>
              </div>
              <Switch
                checked={!!settings.auto_pack_on_slip_print}
                onCheckedChange={(value) => setSettings({ ...settings, auto_pack_on_slip_print: value })}
                data-testid="auto-pack-on-print-switch"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-wt-text-primary">Drivers Can Mark Failed</Label>
                <p className="text-xs text-wt-text-muted">Show the "Can't Deliver?" button in the driver app. Turn off to prevent drivers marking deliveries as failed by mistake</p>
              </div>
              <Switch
                checked={settings.driver_can_mark_failed !== false}
                onCheckedChange={(value) => setSettings({ ...settings, driver_can_mark_failed: value })}
                data-testid="driver-can-mark-failed-switch"
              />
            </div>

            <div>
              <Label className="text-wt-text-primary">Sync Interval (minutes)</Label>
              <Input
                type="number"
                min="1"
                max="60"
                value={settings.order_sync_interval}
                onChange={(e) => setSettings({ ...settings, order_sync_interval: parseInt(e.target.value) || 5 })}
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-2 w-32"
                disabled={!settings.auto_sync_enabled}
              />
              <p className="text-xs text-wt-text-muted mt-1">Recommended: 5-15 minutes</p>
            </div>
          </div>
        </motion.div>

        {/* Push Notifications Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <Bell className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Push Notifications</h2>
              <p className="text-sm text-wt-text-secondary">Get notified about important events</p>
            </div>
          </div>

          <PushNotificationManager />
        </motion.div>
        
        {/* Order Tracking Page Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.42 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <MapPin className="h-6 w-6 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Order Tracking Page</h2>
              <p className="text-sm text-wt-text-secondary">Customer-facing tracking page settings</p>
            </div>
          </div>
          
          <p className="text-wt-text-muted text-sm mb-4">
            Configure your branded order tracking page with live map tracking, driver details, tips, and cross-sell products.
          </p>
          
          <Button
            variant="outline"
            className="w-full border-wt-border text-wt-text-primary hover:bg-wt-subtle"
            onClick={() => navigate('/settings/tracking')}
          >
            <Settings className="h-4 w-4 mr-2" />
            Configure Tracking Page
          </Button>
        </motion.div>

        {/* Payment Methods Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.44 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <CreditCard className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Payment Methods</h2>
              <p className="text-sm text-wt-text-secondary">Methods for "payment already received" orders</p>
            </div>
          </div>

          <p className="text-wt-text-muted text-sm mb-4">
            Manage the payment methods (Bank Transfer, Cash, Card, etc.) staff can select when creating an order that was already paid. These orders stay "Pending Verification" until approved in Cash Management.
          </p>

          <Button
            variant="outline"
            className="w-full border-wt-border text-wt-text-primary hover:bg-wt-subtle"
            onClick={() => navigate('/settings/payment-methods')}
            data-testid="configure-payment-methods-btn"
          >
            <Settings className="h-4 w-4 mr-2" />
            Manage Payment Methods
          </Button>
        </motion.div>

        {/* noon Marketplace */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.46 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <Settings className="h-6 w-6 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">noon Marketplace</h2>
              <p className="text-sm text-wt-text-secondary">Orders, shipments & inventory sync</p>
            </div>
          </div>

          <p className="text-wt-text-muted text-sm mb-4">
            Connect your noon partner account, map your noon warehouses (bulky / courier / express) to app warehouses, and sync inventory automatically. noon orders flow into the Orders page.
          </p>

          <Button
            variant="outline"
            className="w-full border-wt-border text-wt-text-primary hover:bg-wt-subtle"
            onClick={() => navigate('/settings/noon')}
            data-testid="configure-noon-btn"
          >
            <Settings className="h-4 w-4 mr-2" />
            Configure noon Integration
          </Button>
        </motion.div>
        
        {/* Payment Link Gateway */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.44 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
          data-testid="payment-link-gateway-section"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <CreditCard className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Payment Links Gateway</h2>
              <p className="text-sm text-wt-text-secondary">Select the payment gateway used for payment links across the app</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Default Payment Gateway</Label>
              <select
                value={settings.payment_link_gateway || 'stripe'}
                onChange={(e) => setSettings({ ...settings, payment_link_gateway: e.target.value })}
                className="w-full h-10 rounded-md border border-wt-border bg-wt-bg text-wt-text-primary px-3"
                data-testid="payment-link-gateway-select"
              >
                <option value="stripe">Stripe</option>
                <option value="mamopay">MamoPay</option>
              </select>
              <p className="text-xs text-wt-text-muted">
                This gateway will be used for: Leads payment links, Order payment links, COD collection links, and all other payment links in the app.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="flex justify-end"
      >
        <Button
          onClick={handleSaveSettings}
          disabled={loading}
          className="bg-wt-gold text-black hover:bg-wt-gold-hover font-semibold px-8"
          data-testid="save-settings-button"
        >
          <Save className="h-4 w-4 mr-2" />
          Save Settings
        </Button>
      </motion.div>
    </div>
  );
};

export default SettingsPage;
